import { useEffect, useRef } from 'react'
import type { useScenePlayer } from '@/hooks/use-scene-player'

/**
 * Keyboard transport for the scene player (ignored while typing): Space
 * play/pause, ←/→ (or K/J) previous/next line, R replay, E edit, Esc stop.
 */
export function usePlayerShortcuts(
  player: ReturnType<typeof useScenePlayer>,
  { onStart, onEdit }: { onStart?: () => void; onEdit?: (lineId: number) => void } = {},
) {
  const latest = useRef({ player, onStart, onEdit })
  latest.current = { player, onStart, onEdit }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return
      const { player, onStart, onEdit } = latest.current
      const idle = player.status === 'idle'
      const action = {
        ' ': () => (idle ? onStart?.() : player.togglePause()),
        ArrowRight: () => !idle && void player.step(1),
        j: () => !idle && void player.step(1),
        ArrowLeft: () => !idle && void player.step(-1),
        k: () => !idle && void player.step(-1),
        r: () => !idle && void player.replay(),
        e: () => {
          if (!player.current || !onEdit) return
          player.pause()
          onEdit(Number(player.current.lineId))
        },
        Escape: () => !idle && player.stop(),
      }[e.key]
      if (!action) return
      e.preventDefault()
      action()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}

function typing(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}
